import React, {
    FC,
    IframeHTMLAttributes,
    useEffect,
    useState,
    useCallback,
    useMemo,
    CSSProperties,
    ReactNode,
} from "react";
import { useScript } from "hooks/useScript";
import Frame from "components/frame";
import { IThreediumAPI, IThreediumError, IThreediumTemplate } from "constants/product-customizations/threedium";
import theme from "styles/theme";
import { makeStyles, Theme } from "@material-ui/core";
import classnames from "classnames";
import { format } from "lib/globalize-util";
import { LinearProgress, ILinearProgressClasses } from "components/product-media/components/linear-progress";
import * as Sentry from "@sentry/browser";
import { DATATEST } from "constants/integration";

const DEFAULT_ID = "threedium_container";
const VERSION = "1.1.52";
const DEFAULT_CAMERA_POSITION = [0, 10.44, 314];
const DEFAULT_FOV = 20;
const DEFAULT_DISTANCE_FACTOR = 1;

interface IProps extends IframeHTMLAttributes<HTMLIFrameElement> {
    /**
     * Required to rerender iframe completely every time a template changes.
     * It eliminates an error with displaying "Unable to load" message
     * after changing a template before complete loading of the previous one.
     *
     * @note One should use `template.distID` as a key
     */
    useFitToView?: boolean;
    key: string;
    template: IThreediumTemplate;
    disableRendering?: boolean;
    disableRenderingText?: string;
    /** Has hint? Default to true */
    hasHint?: boolean;
    classes?: {
        iframe?: string;
        wrapper?: string;
        hint?: string;
        progressWrapper?: string;
        progressClasses?: ILinearProgressClasses;
    };
    /** Version of the treedium API. Default to 1.1.52 */
    version?: string;
    /**Id of the threedium container to init canvas. Default to "threedium_container"*/
    id?: string;
    /** pass props to the treedium canvas container */
    containerStyle?: CSSProperties;
    onClick?: () => void;
    apiRef?(api: IThreediumAPI, options: IThreediumTemplate): void;
    onModelError?: (err: IThreediumError) => void;
    onLoadError?: (err: ErrorEvent) => void;
    onModelLoad?: () => void;
    distanceFactor?: number;
    errorComponent?: (error: string) => ReactNode;
    progressComponent?: (progress: number) => ReactNode;
}

export const Player3D: FC<IProps> = (props) => {
    const {
        useFitToView,
        id = DEFAULT_ID,
        distanceFactor = DEFAULT_DISTANCE_FACTOR,
        version = VERSION,
        apiRef,
        onModelError,
        onModelLoad,
        onLoadError,
        containerStyle,
        template,
        hasHint = true,
        classes: propsClasses,
        onClick,
        disableRendering = false,
        disableRenderingText = format("We do not support 3D images in mobile browser"),
        errorComponent,
        progressComponent,
        ...rest
    } = props;

    const [ref, setRef] = useState<HTMLIFrameElement | null>(null);
    const [Unlimited3D, setUnlimited3D] = useState<IThreediumAPI | null>(null);

    const [sceneLoading, setSceneLoading] = useState<boolean>(false);
    const [sceneProgress, setSceneProgress] = useState<number>(0);
    const [initializationError, setInitializationError] = useState<string | null>(null);
    const [showHint, setShowHint] = useState(false);
    const [showPointer, setShowPointer] = useState<boolean>(false);

    const handleSetSceneLoading = useCallback((loading: boolean) => {
        setSceneProgress(0);
        setSceneLoading(loading);
    }, []);

    const setPointerCursor = useCallback(() => {
        setShowPointer(true);
    }, []);

    const resetCursor = useCallback(() => {
        setShowPointer(false);
    }, []);

    useEffect(() => {
        setInitializationError(null);
        setShowHint(false);
        handleSetSceneLoading(false);
    }, [template?.distID]); // eslint-disable-line react-hooks/exhaustive-deps

    const [scriptLoading, scriptLoadingError] = useScript({
        root: ref?.contentWindow?.document,
        src: `https://distcdn.unlimited3d.com/pres/v/${version}/unlimited3d.min.js`,
    });

    useEffect(() => {
        if (ref && !scriptLoading && !scriptLoadingError) {
            const currentUnlimited3D = (ref?.contentWindow as any)?.Unlimited3D;
            apiRef?.(currentUnlimited3D, { ...template, containerID: id, version });
            setUnlimited3D(currentUnlimited3D);
        } else if (scriptLoadingError) {
            onLoadError?.(scriptLoadingError);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, scriptLoading, scriptLoadingError, apiRef, onLoadError]);

    useEffect(
        () => {
            if (!Unlimited3D || !template || scriptLoading || scriptLoadingError) {
                setShowHint(false);
                handleSetSceneLoading(false);
                setInitializationError(null);
                return;
            }

            handleSetSceneLoading(true);

            Unlimited3D.init(
                {
                    distID: template.distID,
                    solution3DName: template.solution3DName,
                    projectName: template.projectName,
                    solution3DID: template.solution3DID,
                    containerID: id,
                    onLoadingChanged: (loading) => setSceneProgress(loading?.progress ?? 0),
                    onPointerClick: () => onClick?.(),
                    onCameraInteraction: () => setShowHint(false),
                },
                {},
                (error) => {
                    handleSetSceneLoading(false);

                    if (error) {
                        setInitializationError(error.errorMessage);
                        setShowHint(false);
                        onModelError?.(error);

                        // Capture loading error
                        Sentry.captureException(error);
                        return;
                    }

                    //Old versions of unlimited3D API doesn't support this method
                    //When does we also should set correct camera position
                    if (useFitToView && Unlimited3D.updateCameraToScene) {
                        Unlimited3D.setCameraOptions({ fov: DEFAULT_FOV }, () => {
                            // @ts-ignore
                            Unlimited3D.setCameraTarget({ target: [0, 0, 0] });
                            // @ts-ignore
                            Unlimited3D.setCameraPosition({ position: DEFAULT_CAMERA_POSITION });
                            Unlimited3D.updateCameraToScene({ distanceFactor: distanceFactor }, (err) => {
                                if (err) {
                                    setInitializationError(err.errorMessage);
                                    onModelError?.(err);
                                }
                            });
                        });
                    }

                    setInitializationError(null);
                    setShowHint(true);
                    onModelLoad?.();
                }
            );
        },
        /* eslint-disable react-hooks/exhaustive-deps */
        [
            Unlimited3D,
            template?.distID,
            template?.solution3DID,
            id,
            onClick,
            scriptLoading,
            scriptLoadingError,
            handleSetSceneLoading,
            onModelError,
            onModelLoad,
        ]
        /* eslint-enable react-hooks/exhaustive-deps */
    );

    const loading = useMemo(() => sceneLoading || scriptLoading, [sceneLoading, scriptLoading]);
    const error = useMemo(
        () => initializationError || scriptLoadingError?.message,
        [initializationError, scriptLoadingError]
    );

    const classes = useStyles({});
    const styles = useMemo(
        () => ({
            container3D: {
                width: "100%",
                height: "100%",
                ...containerStyle,
                cursor: showPointer && "pointer",
            },
        }),
        [containerStyle, showPointer]
    );

    if (disableRendering) {
        return (
            <div
                className={classnames(classes.wrapper, classes.displayCenter, propsClasses?.wrapper)}
                data-test={DATATEST.PLAYER_3D.PLAYER}
            >
                <span>{disableRenderingText}</span>
            </div>
        );
    }

    const renderProgress = (): ReactNode => {
        if (progressComponent) {
            return progressComponent(sceneProgress);
        }

        return (
            <LinearProgress
                value={sceneProgress}
                label={format("Loading 3D Model")}
                classes={propsClasses?.progressClasses}
            />
        );
    };

    const renderError = (): ReactNode => {
        if (errorComponent) {
            return errorComponent(error);
        }

        return (
            <span className={classes.error}>
                {format("Error")}: {error}
            </span>
        );
    };

    return (
        <div className={classnames(classes.wrapper, propsClasses?.wrapper)} data-test={DATATEST.PLAYER_3D.PLAYER}>
            <Frame
                {...rest}
                frameRef={setRef}
                className={classnames(classes.iframe, propsClasses?.iframe)}
                data-test={DATATEST.PLAYER_3D.THREEDIUM_IFRAME}
            >
                <div
                    style={styles.container3D}
                    id={id}
                    data-test={DATATEST.PLAYER_3D.THREEDIUM_CONTAINER}
                    onMouseDown={setPointerCursor}
                    onMouseUp={resetCursor}
                />
            </Frame>

            {loading && !error && (
                <div className={classnames(classes.absoluteFullSizeWrapper, propsClasses?.progressWrapper)}>
                    {renderProgress()}
                </div>
            )}

            {hasHint && !error && (
                <div
                    data-test={DATATEST.PLAYER_3D.HINT}
                    className={classnames(classes.hint, { visible: showHint }, propsClasses?.hint)}
                >
                    {format("Click and drag to rotate and zoom")}
                </div>
            )}

            {error && <div className={classes.absoluteFullSizeWrapper}>{renderError()}</div>}
        </div>
    );
};

const useStyles = makeStyles<Theme, {}>({
    wrapper: {
        width: "100%",
        height: "100%",
        position: "relative",
    },
    displayCenter: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
    },
    iframe: {
        width: "100%",
        height: "100%",
    },
    hint: {
        display: "flex",
        alignItems: "center",
        position: "absolute",
        bottom: 0,
        padding: 10,
        backgroundColor: "rgba(245,245,245,0.7)",
        borderRadius: "8px 8px 0 0",
        fontSize: 14,
        color: theme.palette.darkGrey,
        opacity: 0,
        transition: "opacity 600ms cubic-bezier(0.23, 1, 0.32, 1) 0ms",
        "&.visible": {
            opacity: 1,
        },
    },
    absoluteFullSizeWrapper: {
        position: "absolute",
        top: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    error: {
        fontSize: 12,
        color: theme.palette.error,
    },
});

export default Player3D;
